import { forwardRef, useEffect, useRef, useState } from "react";

interface Props {
  onSend: (text: string) => void;
  disabled?: boolean;
  /** Opens the paste-an-email form. */
  onAttachEmail: () => void;
  /** Subject of the email currently attached to the conversation, if any. */
  attachedSubject?: string | null;
  onClearEmail: () => void;
}

/** Growing textarea, not an input.
 *
 *  An <input> is one line and silently scrolls, which is wrong for the two things
 *  people actually type here: a policy question long enough to be specific, and a
 *  revision instruction that quotes part of a draft. A textarea that grows to a cap
 *  shows the whole thing, and Shift+Enter gives them a way to structure it. */
export const Composer = forwardRef<HTMLTextAreaElement, Props>(function Composer(
  { onSend, disabled, onAttachEmail, attachedSubject, onClearEmail },
  ref,
) {
  const [value, setValue] = useState("");
  const inner = useRef<HTMLTextAreaElement | null>(null);

  // Re-measure on every change. COLLAPSE TO ZERO FIRST, not to "auto".
  //
  // scrollHeight is bounded below by the element's current layout height, so
  // measuring without collapsing means the box only ever grows — a deleted line
  // leaves its row behind. "auto" is the usual way to collapse it and is WRONG
  // here: this textarea is a flex item, so height:auto lets it stretch to the
  // container and scrollHeight comes back as the height of the whole composer
  // (measured at 916px on a 820px viewport, then clamped by max-height into a
  // permanently six-line box). Zero is unambiguous in any layout context.
  useEffect(() => {
    const el = inner.current;
    if (!el) return;
    el.style.height = "0px";
    el.style.height = `${el.scrollHeight}px`;
  }, [value]);

  function submit() {
    const t = value.trim();
    if (!t || disabled) return;
    onSend(t);
    setValue("");
  }

  return (
    <div className="composer">
      <div className="composer-inner">
        {attachedSubject !== null && attachedSubject !== undefined ? (
          <div className="email-pill">
            <span aria-hidden="true">✉️</span>
            <span className="grow">
              Replying to <b>{attachedSubject || "(no subject)"}</b>
            </span>
            <button type="button" className="icon-btn" onClick={onClearEmail} aria-label="Remove the attached email" title="Remove">
              ✕
            </button>
          </div>
        ) : (
          <div className="composer-tools">
            <button type="button" className="tool-btn" onClick={onAttachEmail}>
              <span aria-hidden="true">✉️</span> Reply to an email
            </button>
          </div>
        )}

        <div className="composer-row">
          <label className="sr-only" htmlFor="composer-input">
            Message the assistant
          </label>
          <textarea
            id="composer-input"
            ref={(node) => {
              inner.current = node;
              if (typeof ref === "function") ref(node);
              else if (ref) ref.current = node;
            }}
            rows={1}
            value={value}
            // Short enough to fit one line at 375px. The longer framing lives in the
            // "Reply to an email" button and the hint below, so nothing is lost by it.
            placeholder="Ask about a policy…"
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => {
              // Enter sends, Shift+Enter makes a newline. The same contract as the
              // feedback box, so there is one rule to learn rather than two.
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                submit();
              }
            }}
          />
          <button type="button" className="send" title="Send" aria-label="Send message" disabled={disabled || !value.trim()} onClick={submit}>
            <svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true">
              <path
                fill="currentColor"
                d="M2.72 2.05a.75.75 0 0 0-.97.94l2.1 6.26 7.65.75-7.65.75-2.1 6.26a.75.75 0 0 0 .97.94l15-6.25a.75.75 0 0 0 0-1.4l-15-6.25Z"
              />
            </svg>
          </button>
        </div>
        <p className="composer-hint">Answers come from Crown's own policy documents. Always review a draft before sending it.</p>
      </div>
    </div>
  );
});
